import React from 'react';
import { styled } from '@mui/material/styles';

import { useGetBaseStepFields } from '../../hooks';
import AgathaMessage from '../../Messages/AgathaMessage';
import UserMessage from '../../Messages/UserMessage';
import { selectIsAuthorIconVisible } from '../../StepWrapper/selectors';
import { useStepContext } from '../../StepWrapper/StepWrapper';
import { ButtonComponent, ButtonOptions, ContextVariables } from '../../types';
import OptionButton from '../../UtilComponents/OptionButton';

interface SingleSelectProps {
  isLastStep: boolean;
  /** Sets the selected option as context variables on the step */
  setStepContextVariables: (contextVariables: ContextVariables) => void;
  /** Sets the step as submitted -> when the user clicks on an option */
  setStepSent: (query?: string) => void;
  stepIndex: number;
  /** Object containing data to display */
  widgetComponent: ButtonComponent;
}

const SingleSelect: React.FC<React.PropsWithChildren<SingleSelectProps>> = ({
  isLastStep,
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}) => {
  const context = useStepContext();
  const { confirmTag, error, isMobile, updateConversation } = context;
  const isAuthorIconVisible = selectIsAuthorIconVisible(context, stepIndex);
  const { contextVariables, isSent } = useGetBaseStepFields(widgetComponent);

  const componentFields = widgetComponent.component_fields;
  const outputVariable = componentFields.output_variable;
  const isReroutedByNegativeFeedback =
    !!widgetComponent.isReroutedByNegativeFeedback;
  const shouldConfirmTag = !!componentFields.should_confirm_tag;

  const selectedOption = componentFields?.options.find(option => {
    return option.value === contextVariables[outputVariable];
  });

  const onSubmit = (selectedValue: string, displayName?: string) => {
    setStepContextVariables({ [outputVariable]: selectedValue });

    // @Nick TODO: Make livechat mode supported
    // // one chats
    // if (isLiveChatMode) {
    //   switch (liveChatIntegration) {
    //     case 'zendesk':
    //       applyZopimFunction(widgetInitMethod, 'sendChatMsg', selectedValue);
    //       break;
    //     case 'kustomer':
    //       applyKustomerCoreFunctionWithCallback('sendMessage', {
    //         body: displayName,
    //         conversationId: chatId,
    //         isInAssistantMode,
    //         payload: selectedValue,
    //       })
    //         .then(() => {
    //           dispatch(
    //             sendUserReply({
    //               addWidgetComponent: false,
    //               message: displayName || selectedValue,
    //             }),
    //           );
    //         })
    //         .catch(() => {
    //           const errorMessage = 'Message failed to send';
    //           dispatch(addScreenReaderNotification(errorMessage));
    //           dispatch(addLiveChatEventMessage({ message: errorMessage }));
    //         });
    //       break;
    //     case 'sunco':
    //       Smooch.sendMessage(
    //         {
    //           // @ts-expect-error Smooch types are outdated, `payload` is actually supported
    //           payload: selectedValue,
    //           text: displayName || selectedValue,
    //           type: 'text',
    //         },
    //         chatId,
    //       );
    //       break;
    //   }
    //   setStepSent();
    //   return;
    // }

    // normal workflow
    if (shouldConfirmTag) {
      confirmTag(selectedValue);
    } else {
      updateConversation({ [outputVariable]: selectedValue });
    }

    setStepSent();
  };

  if (isReroutedByNegativeFeedback) {
    return (
      <AgathaMessage
        isAuthorIconVisible={isAuthorIconVisible}
        stepIndex={stepIndex}
      >
        {componentFields.prompt}
      </AgathaMessage>
    );
  }

  // `!isLastStep` check is needed to avoid rendering options for previous
  // option steps that weren't sent.
  // It can happen with Kustomer One Chat, for example (it can send an option
  // step at any point in a conversation).
  if (isSent || !isLastStep) {
    return (
      <>
        <AgathaMessage
          agentIcon={componentFields.agentIcon}
          agentName={componentFields.agentName}
          hideTimestamp={widgetComponent.hideTimestamp}
          isAuthorIconVisible={isAuthorIconVisible}
          isLiveChat={widgetComponent.isInAgentConvo}
          stepIndex={stepIndex}
          timestamp={widgetComponent.timestamp}
        >
          {componentFields.prompt}
        </AgathaMessage>
        {selectedOption?.display_name && (
          <UserMessage stepIndex={stepIndex}>
            {selectedOption.display_name}
          </UserMessage>
        )}
      </>
    );
  }

  return (
    <Container>
      <AgathaMessage
        agentIcon={componentFields.agentIcon}
        agentName={componentFields.agentName}
        hideTimestamp={widgetComponent.hideTimestamp}
        isAuthorIconVisible={isAuthorIconVisible}
        isLiveChat={widgetComponent.isInAgentConvo}
        stepIndex={stepIndex}
        timestamp={widgetComponent.timestamp}
      >
        {componentFields.prompt}
      </AgathaMessage>
      <ContentContainer>
        {componentFields?.options.map((option: ButtonOptions) => (
          <OptionButton
            isDisabled={Boolean(error)}
            isMobile={isMobile}
            key={option.value}
            onClick={() => onSubmit(option.value, option.display_name)}
            text={option.display_name}
            url={option.url}
            urlTarget={option.url_config?.target_attribute}
          />
        ))}
      </ContentContainer>
    </Container>
  );
};

export default SingleSelect;

const Container = styled('form')`
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-bottom: 16px;
  padding-left: 24px;
  margin-top: 4px;
`;
